import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Users, BookOpen, ClipboardList, Plus, Loader2, RotateCcw, UserPlus } from "lucide-react";

import { GET_DASHBOARD_STATS } from "@/graphql/queries/dashboard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/AuthContext";

export default function DashboardPage() {
  const { data, loading } = useQuery(GET_DASHBOARD_STATS);
  const { isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const stats = data?.dashboardStats ?? {
    studentCount: 0,
    sessionCount: 0,
    completedSessionCount: 0,
    recentSessions: [],
  };

  // Issue #66: a grown-up with no child yet has exactly one useful thing to do
  // here, and three zero counters and „Nauja sesija" (which needs a child) are
  // not it. Only when the stats actually arrived — a failed query must not read
  // as an empty household — and never for an admin, whose dashboard is the
  // instance rather than a family.
  if (data?.dashboardStats && stats.studentCount === 0 && !isAdmin) {
    return <FirstStep />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pradžia</h1>
        <p className="text-muted-foreground">
          Sveiki atvykę į Scio - mokinių įgūdžių sekimo sistemą
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Iš viso mokinių</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.studentCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Iš viso sesijų</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.sessionCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Užbaigtos sesijos</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completedSessionCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Greiti veiksmai</CardTitle>
          <CardDescription>Pradėkite darbą su mokiniais</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {(() => {
            const lastWithExercises = stats.recentSessions.find(
              (s: any) => s.exerciseCount > 0 && s.subject && s.student
            );
            if (!lastWithExercises) return null;
            const s = lastWithExercises;
            return (
              <Button asChild>
                <Link to={`/sessions/new?studentId=${s.student.id}&subjectId=${s.subjectKey}`}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {s.subject.name} – {s.student.name} ({s.exerciseCount} pr.)
                </Link>
              </Button>
            );
          })()}
          <Button variant={stats.recentSessions.some((s: any) => s.exerciseCount > 0 && s.subject) ? "outline" : "default"} asChild>
            <Link to="/sessions/new">
              <Plus className="mr-2 h-4 w-4" />
              Nauja sesija
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/students/new">
              <Plus className="mr-2 h-4 w-4" />
              Pridėti mokinį
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Recent Sessions */}
      {(() => {
        const pending = stats.recentSessions.filter((s: any) => s.status !== "COMPLETED");
        const completed = stats.recentSessions.filter((s: any) => s.status === "COMPLETED");

        const renderSession = (session: any, muted = false) => (
          <div
            key={session.id}
            className={`flex items-center justify-between rounded-lg border p-3${muted ? " opacity-60" : ""}`}
          >
            <div>
              <p className="font-medium">{session.student.name}</p>
              <p className="text-sm text-muted-foreground">
                {new Date(session.createdAt).toLocaleDateString("lt-LT")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm capitalize">
                {session.status === "DRAFT" && "Juodraštis"}
                {session.status === "GENERATED" && "Sugeneruota"}
                {session.status === "GRADED" && "Įvertinta"}
                {session.status === "COMPLETED" && "Užbaigta"}
              </span>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/sessions/${session.id}`}>Peržiūrėti</Link>
              </Button>
            </div>
          </div>
        );

        return (
          <Card>
            <CardHeader>
              <CardTitle>Paskutinės sesijos</CardTitle>
              <CardDescription>Naujausios mokymosi sesijos</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.recentSessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nėra sesijų. Sukurkite pirmąją sesiją!
                </p>
              ) : (
                <div className="space-y-4">
                  {pending.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nebaigtos</p>
                      {pending.map((s: any) => renderSession(s))}
                    </div>
                  )}
                  {completed.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Užbaigtos</p>
                      {completed.map((s: any) => renderSession(s, true))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}

function FirstStep() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sveiki atvykę į Scio!</h1>
        <p className="text-muted-foreground">Pradėkime nuo vieno žingsnio.</p>
      </div>

      <Card className="max-w-2xl border-primary/40 bg-primary/5">
        <CardHeader>
          <UserPlus className="mb-2 h-8 w-8 text-primary" aria-hidden="true" />
          <CardTitle className="text-xl">Sukurkite vaiko profilį</CardTitle>
          <CardDescription>
            Įrašykite vaiko vardą ir gimimo metus — pagal juos parinksime klasę ir
            pirmąsias užduotis. Po to galėsite sudaryti pirmąją sesiją.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild size="lg">
            <Link to="/students/new">
              <Plus className="mr-2 h-4 w-4" />
              Sukurti vaiko profilį
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
